import React, { useEffect, useState } from "react";
import {
  ArrowRight, Sparkles, Clock, FileCheck, Mail, Megaphone, HardHat, ShieldCheck,
  Search, Target, CheckCircle2, Landmark, TrendingUp, Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Pricing from "@/components/Pricing";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { NICHE_IMAGES } from "@/lib/content";
import { daysLeft } from "@/lib/format";

const NICHE_META = [
  { key: "communication", icon: Megaphone, label: "Communication, Événementiel & Formation",
    desc: "Agences, imprimeurs, organisateurs et formateurs. Les prestations intellectuelles figurent parmi les marchés les plus nombreux." },
  { key: "batiment", icon: HardHat, label: "Second Œuvre & Rénovation Énergétique",
    desc: "Isolation, chauffage, menuiserie, électricité. Le gros volume, porté par la rénovation du patrimoine public." },
  { key: "numerique", icon: ShieldCheck, label: "Numérique & Cybersécurité",
    desc: "Développeurs, intégrateurs, sociétés de services. Premier poste d'achat de l'État, avec une cybersécurité en forte hausse." },
];

export default function Landing() {
  const { login } = useAuth();
  const [yearly, setYearly] = useState(false);
  const [demoNiche, setDemoNiche] = useState("communication");
  const [demo, setDemo] = useState(null);
  const [loadingDemo, setLoadingDemo] = useState(true);
  const [reviews, setReviews] = useState([]);
  // Section avis en standby tant qu'il n'y a pas assez de clients. Passer à true pour la réafficher.
  const SHOW_REVIEWS = false;

  useEffect(() => {
    api.get("/public/reviews").then(({ data }) => setReviews(data.reviews || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    setLoadingDemo(true);
    api.get(`/public/demo?niche=${demoNiche}`).then(({ data }) => {
      if (active) { setDemo(data); setLoadingDemo(false); }
    }).catch(() => active && setLoadingDemo(false));
    return () => { active = false; };
  }, [demoNiche]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-60" />
        <div className="max-w-7xl mx-auto px-5 pt-16 pb-20 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center relative">
          <div className="animate-rise">
            <div className="inline-flex items-center gap-2 text-xs font-mono font-bold tracking-[0.15em] uppercase text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200">
              <Landmark className="w-3.5 h-3.5" /> 233 milliards d'euros de marchés publics en 2024
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Trouvez votre prochain marché public,<br />
              <span className="text-primary">sans y passer vos matinées.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Chaque matin, nous parcourons pour vous les marchés publics et ne gardons que ceux qui
              correspondent vraiment à votre métier. Vous recevez deux ou trois opportunités, avec une fiche
              claire qui vous dit en quoi le marché est fait pour vous et jusqu'à quand répondre.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={login} className="rounded-full text-base h-12 px-7" data-testid="hero-cta-btn">
                Commencer, 0€ aujourd'hui <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => document.getElementById("fonctionnement")?.scrollIntoView({ behavior: "smooth" })} className="rounded-full text-base h-12 px-7" data-testid="hero-secondary-btn">
                Découvrir le service
              </Button>
            </div>
            <p className="mt-3 text-sm text-slate-500">Déjà inscrit&nbsp;? <button onClick={login} className="text-primary font-semibold hover:underline" data-testid="hero-login-link">Connectez-vous</button></p>
            <div className="mt-6 flex items-center gap-5 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> 7 jours offerts</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Résiliable à tout moment</span>
            </div>
          </div>

          <div className="animate-rise" style={{ animationDelay: "0.12s" }}>
            <div className="rounded-2xl bg-white border border-border shadow-2xl shadow-slate-200/70 overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-slate-50">
                <div className="flex gap-1.5"><span className="w-3 h-3 rounded-full bg-red-400" /><span className="w-3 h-3 rounded-full bg-amber-400" /><span className="w-3 h-3 rounded-full bg-emerald-400" /></div>
                <div className="flex items-center gap-2 ml-2 text-xs font-mono text-slate-500"><Mail className="w-3.5 h-3.5" /> Votre email de 8h00</div>
              </div>
              <div className="p-5">
                <div className="flex gap-2 mb-4 flex-wrap">
                  {NICHE_META.map((n) => (
                    <button key={n.key} onClick={() => setDemoNiche(n.key)} data-testid={`demo-niche-${n.key}`}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${demoNiche === n.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-border hover:border-slate-400"}`}>
                      {n.key === "communication" ? "Communication" : n.key === "batiment" ? "Bâtiment" : "Numérique"}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mb-3">Bonjour, voici vos marchés du jour :</p>
                <div className="space-y-3 min-h-[260px]">
                  {loadingDemo && <div className="flex items-center justify-center h-40"><div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}
                  {!loadingDemo && demo?.tenders?.length === 0 && (
                    <p className="text-sm text-slate-400 py-10 text-center">Aucun marché pour l'instant, revenez demain matin.</p>
                  )}
                  {!loadingDemo && demo?.tenders?.map((t, i) => {
                    const dl = daysLeft(t.date_limite);
                    return (
                      <div key={i} className="rounded-xl border border-border p-4 bg-slate-50/60 hover:bg-white transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-mono font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">PERTINENCE {t.score}%</span>
                          {dl != null && dl >= 0 && <span className="text-[11px] font-mono text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" />J-{dl}</span>}
                        </div>
                        <p className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2">{t.objet}</p>
                        <p className="text-xs text-slate-500 mt-1.5">{t.acheteur} · {(t.departements || []).join(", ") || "France"}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400 font-mono">
                  <Sparkles className="w-3 h-3 text-primary" /> Marchés réels du BOAMP, sélectionnés en direct
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-20" />
        <div className="max-w-7xl mx-auto px-5 relative">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Search, t: "Trop d'annonces, partout", d: "BOAMP, journaux locaux, profils d'acheteurs. Des centaines de milliers de marchés par an, impossibles à suivre à la main." },
              { icon: Clock, t: "Pas une minute à perdre", d: "Vous dirigez une entreprise, vous ne lisez pas 50 annonces par semaine. Les bons marchés passent, faute de temps." },
              { icon: FileCheck, t: "Des dossiers décourageants", d: "Un cahier des charges, c'est parfois 80 pages pour savoir si cela vaut le coup. Nous le résumons pour vous." },
            ].map((b, i) => (
              <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-7 backdrop-blur">
                <b.icon className="w-8 h-8 text-blue-400 mb-4" />
                <h3 className="font-display text-xl font-bold mb-2">{b.t}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 justify-between">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-extrabold">Un seul marché gagné rembourse des années d'abonnement.</h3>
              <p className="text-blue-100 mt-2">Les marchés accessibles aux TPE et PME vont souvent de 20 000 à 200 000 euros. L'acheteur public règle sous 30 jours et ne fait pas faillite.</p>
            </div>
            <Button size="lg" onClick={login} className="bg-white text-blue-700 hover:bg-blue-50 rounded-full shrink-0 h-12 px-7" data-testid="value-cta-btn">
              Commencer, 0€ aujourd'hui
            </Button>
          </div>
        </div>
      </section>

      <section id="fonctionnement" className="py-20 max-w-7xl mx-auto px-5">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-mono font-bold tracking-[0.2em] uppercase text-blue-700 bg-blue-50 px-2.5 py-1 rounded border border-blue-200">Comment ça marche</span>
          <h2 className="mt-5 font-display text-3xl md:text-4xl font-extrabold text-slate-900">De milliers d'annonces à deux marchés faits pour vous</h2>
          <p className="mt-3 text-slate-600">Tout se fait automatiquement. De votre côté, vous lisez simplement votre email du matin.</p>
        </div>
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { icon: Target, t: "1. Vous vous présentez", d: "Votre métier, vos départements, la taille de marché qui vous intéresse. Deux minutes suffisent." },
            { icon: Search, t: "2. Nous cherchons", d: "Les marchés du BOAMP sont parcourus deux fois par jour, 7j/7. Rien ne vous échappe." },
            { icon: Sparkles, t: "3. Nous trions et expliquons", d: "Chaque marché reçoit un indice de pertinence, un résumé clair, son budget, sa date limite et ses critères." },
            { icon: Mail, t: "4. Vous recevez l'essentiel", d: "Chaque matin, deux ou trois marchés au maximum, avec des rappels avant chaque échéance." },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl bg-white border border-border p-6 hover-lift">
              <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-4"><s.icon className="w-6 h-6 text-primary" /></div>
              <h3 className="font-display font-bold text-lg text-slate-900 mb-1.5">{s.t}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="secteurs" className="py-20 bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-5">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-mono font-bold tracking-[0.2em] uppercase text-blue-700 bg-blue-50 px-2.5 py-1 rounded border border-blue-200">Secteurs couverts</span>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Spécialisé sur votre métier, pas sur « tous les marchés »</h2>
            <p className="mt-3 text-slate-600">Nous connaissons votre secteur et parlons votre langue. Le tri est pensé pour votre réalité de terrain.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {NICHE_META.map((n) => (
              <div key={n.key} className="rounded-2xl overflow-hidden border border-border bg-white hover-lift group" data-testid={`sector-card-${n.key}`}>
                <div className="h-44 overflow-hidden relative">
                  <img src={NICHE_IMAGES[n.key]} alt={n.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  <n.icon className="absolute bottom-4 left-4 w-7 h-7 text-white" />
                </div>
                <div className="p-6">
                  <h3 className="font-display font-bold text-lg text-slate-900 mb-2">{n.label}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{n.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {SHOW_REVIEWS && reviews.length > 0 && (
        <section className="py-20 max-w-7xl mx-auto px-5">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-mono font-bold tracking-[0.2em] uppercase text-blue-700 bg-blue-50 px-2.5 py-1 rounded border border-blue-200">Ils nous font confiance</span>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Ce que disent nos clients</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <div key={i} className="rounded-2xl bg-white border border-border p-7">
                <div className="flex items-center gap-1 text-amber-400 mb-4">{"★".repeat(r.rating)}</div>
                <p className="text-slate-700 leading-relaxed">«&nbsp;{r.quote}&nbsp;»</p>
                <p className="text-sm font-semibold text-slate-900 mt-4">{r.name}{r.role ? <span className="font-normal text-slate-500"> — {r.role}</span> : null}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="py-20 max-w-7xl mx-auto px-5">
        <div className="rounded-3xl border border-border bg-white p-8 md:p-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { n: "233 Mds €", l: "de marchés publics passés en 2024" },
              { n: "223 000+", l: "marchés recensés sur l'année" },
              { n: "60 %", l: "des marchés attribués à des PME" },
              { n: "2×/jour", l: "actualisation des annonces, 7j/7" },
            ].map((s, i) => (
              <div key={i}>
                <p className="font-display text-3xl md:text-4xl font-extrabold text-primary">{s.n}</p>
                <p className="text-sm text-slate-600 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-400 mt-8">
            Sources : Bercy et Observatoire économique de la commande publique, données 2024. Diffusion officielle BOAMP (DILA) sous Licence Ouverte Etalab 2.0.
          </p>
        </div>
      </section>

      <section id="tarifs" className="py-20 bg-slate-50 border-y border-border">
        <div className="max-w-7xl mx-auto px-5">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-mono font-bold tracking-[0.2em] uppercase text-blue-700 bg-blue-50 px-2.5 py-1 rounded border border-blue-200">Tarifs</span>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Un investissement minuscule face à un marché gagné</h2>
            <p className="mt-3 text-slate-600">Vous ne payez rien aujourd'hui. Votre carte est demandée pour démarrer l'essai, mais l'abonnement ne démarre qu'après 7 jours, et reste résiliable en un clic à tout moment.</p>
          </div>
          <Pricing yearly={yearly} setYearly={setYearly} onSelect={() => login()} />
        </div>
      </section>

      <section id="faq" className="py-20 max-w-3xl mx-auto px-5">
        <div className="text-center mb-10">
          <span className="text-xs font-mono font-bold tracking-[0.2em] uppercase text-blue-700 bg-blue-50 px-2.5 py-1 rounded border border-blue-200">Questions fréquentes</span>
          <h2 className="mt-5 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Transparent et dans les règles</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {[
            { q: "D'où viennent les marchés ?", a: "Du BOAMP, le Bulletin officiel des annonces des marchés publics, publié par la DILA, un service de l'État. Ces données sont réutilisables à des fins commerciales sous Licence Ouverte Etalab 2.0, à condition d'en mentionner la source, ce que nous faisons." },
            { q: "Est-ce légal de proposer ce service ?", a: "Oui. La Licence Ouverte autorise explicitement la réutilisation, y compris commerciale. Nous ne revendons pas la donnée brute : nous vous apportons un service de sélection et de mise en clair." },
            { q: "Le BOAMP couvre-t-il tous les marchés ?", a: "La publication au BOAMP est obligatoire au-dessus des seuils européens. Les plus petits marchés paraissent aussi dans la presse locale ou sur les profils d'acheteurs. Pour démarrer, le BOAMP couvre déjà l'essentiel des marchés accessibles aux TPE et PME, et nous élargissons progressivement les sources." },
            { q: "Les montants sont-ils fiables ?", a: "Quand l'acheteur publie un montant, nous l'affichons. Sinon, nous proposons une fourchette estimée, clairement signalée comme une estimation." },
            { q: "Comment récupérer le dossier de consultation ?", a: "Le dossier se télécharge sur la plateforme de l'acheteur, souvent avec création d'un compte. Nous vous renvoyons directement vers l'avis officiel et le profil de l'acheteur." },
            { q: "Et mes données d'entreprise ?", a: "Hébergement en Union européenne, désinscription en un clic. Nous ne vous demandons jamais votre mot de passe ni vos coordonnées bancaires par email." },
          ].map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="bg-white border border-border rounded-xl px-5" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="py-16 max-w-7xl mx-auto px-5">
        <div className="rounded-3xl bg-slate-900 text-white p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay opacity-20" />
          <div className="relative">
            <TrendingUp className="w-10 h-10 text-blue-400 mx-auto mb-5" />
            <h2 className="font-display text-3xl md:text-4xl font-extrabold max-w-2xl mx-auto">Votre prochain marché est peut-être dans votre email de demain matin.</h2>
            <Button size="lg" onClick={login} className="mt-8 bg-blue-600 hover:bg-blue-500 rounded-full h-12 px-8 text-base" data-testid="final-cta-btn">
              Commencer, 0€ aujourd'hui <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <p className="mt-4 text-xs text-slate-400 flex items-center justify-center gap-1.5"><Heart className="w-3.5 h-3.5" /> 7 jours offerts, puis votre formule, résiliable à tout moment</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
